import { useLocalSearchParams, useRouter } from "expo-router";
import { AlertTriangle, ArrowRight, ContactRound, UserRound } from "./central-icons";
import { useEffect, useRef, useState } from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";

import {
  type CentralCaseRecord,
  listCentralCases,
} from "../lib/central-cases";
import { buildCentralCaseWorkspacePath } from "../lib/central-case-context";
import { subscribeCentralCaseChanges } from "../lib/central-case-events";
import { central, spacing } from "../theme/tokens";

type CaseIdentityState =
  | { status: "idle" }
  | { caseId: string; status: "loading" }
  | { caseId: string; record: CentralCaseRecord; status: "ready" }
  | { caseId: string; status: "invalid" };

export function CentralCaseIdentityBand() {
  const router = useRouter();
  const params = useLocalSearchParams<{ caseId?: string | string[] }>();
  const requestedCaseId = normalizeCaseId(params.caseId);
  const requestSequence = useRef(0);
  const [state, setState] = useState<CaseIdentityState>({ status: "idle" });

  useEffect(() => {
    const sequence = requestSequence.current + 1;
    requestSequence.current = sequence;
    if (!requestedCaseId) {
      setState({ status: "idle" });
      return;
    }

    let active = true;
    setState({ caseId: requestedCaseId, status: "loading" });
    void listCentralCases()
      .then((cases) => {
        if (!active || sequence !== requestSequence.current) return;
        const record = cases.find((candidate) => candidate.id === requestedCaseId);
        setState(
          record
            ? { caseId: requestedCaseId, record, status: "ready" }
            : { caseId: requestedCaseId, status: "invalid" },
        );
      })
      .catch(() => {
        if (!active || sequence !== requestSequence.current) return;
        setState({ caseId: requestedCaseId, status: "invalid" });
      });

    return () => {
      active = false;
    };
  }, [requestedCaseId]);

  useEffect(() => subscribeCentralCaseChanges((record) => {
    if (record.id !== requestedCaseId) return;
    setState({ caseId: requestedCaseId, record, status: "ready" });
  }), [requestedCaseId]);

  if (state.status === "idle") return null;

  if (state.status === "loading") {
    return (
      <View
        accessibilityLiveRegion="polite"
        testID="central-case-identity-band"
        style={styles.band}
      >
        <Text style={styles.loadingText}>Loading case context…</Text>
      </View>
    );
  }

  if (state.status === "invalid") {
    return (
      <View
        accessibilityLiveRegion="assertive"
        testID="central-case-identity-band"
        style={[styles.band, styles.invalidBand]}
      >
        <View style={styles.invalidCopy}>
          <AlertTriangle color={central.status.warning.foreground} size={18} />
          <View style={styles.copyStack}>
            <Text style={styles.invalidTitle}>Case context unavailable</Text>
            <Text style={styles.invalidDescription}>
              The requested case could not be found in your current location.
            </Text>
          </View>
        </View>
        <Pressable
          accessibilityRole="button"
          onPress={() => router.push("/workspace/case-list" as never)}
          style={styles.invalidAction}
        >
          <Text style={styles.invalidActionText}>Choose a case</Text>
          <ArrowRight color={central.action.primary} size={16} />
        </Pressable>
      </View>
    );
  }

  const { record } = state;
  const links = [
    { label: "Overview", screenId: "case-overview" },
  ] as const;

  return (
    <View
      accessibilityLabel={`Active case: ${record.lovedOneName}`}
      testID="central-case-identity-band"
      style={styles.band}
    >
      <View style={styles.identity}>
        <View style={styles.primaryIdentity}>
          <Text numberOfLines={1} style={styles.lovedOneName}>
            {record.lovedOneName}
          </Text>
          <View style={styles.statusBadge}>
            <Text style={styles.statusText}>{formatCaseStatus(record.status)}</Text>
          </View>
        </View>
        <View style={styles.metadata}>
          <View style={styles.metadataItem}>
            <ContactRound color={central.text.muted} size={15} />
            <Text numberOfLines={1} style={styles.metadataText}>
              {record.primaryContact || "Primary contact not recorded"}
            </Text>
          </View>
          <View style={styles.metadataItem}>
            <UserRound color={central.text.muted} size={15} />
            <Text numberOfLines={1} style={styles.metadataText}>
              {record.ownerName || "Unassigned"}
            </Text>
          </View>
        </View>
      </View>
      <ScrollView
        contentContainerStyle={styles.links}
        horizontal
        showsHorizontalScrollIndicator={false}
      >
        {links.map((link) => (
          <Pressable
            accessibilityLabel={`Open ${link.label} for ${record.lovedOneName}`}
            accessibilityRole="link"
            key={link.screenId}
            onPress={() =>
              router.push(
                buildCentralCaseWorkspacePath(link.screenId, record.id) as never,
              )
            }
            style={styles.link}
          >
            <Text style={styles.linkText}>{link.label}</Text>
          </Pressable>
        ))}
      </ScrollView>
    </View>
  );
}

export function normalizeCaseId(value: string | string[] | undefined) {
  return (Array.isArray(value) ? value[0] : value)?.trim() ?? "";
}

function formatCaseStatus(value: CentralCaseRecord["status"]) {
  return value.replace(/_/g, " ").replace(/^./, (letter) => letter.toUpperCase());
}

const styles = StyleSheet.create({
  band: {
    alignItems: "center",
    backgroundColor: central.surface.raised,
    borderBottomColor: central.border.default,
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
    justifyContent: "space-between",
    minHeight: 72,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  caseId: {
    color: central.text.muted,
    fontFamily: central.typography.bodyMedium,
    fontSize: 12,
  },
  copyStack: { flex: 1, gap: 2 },
  identity: { flex: 1, gap: spacing.xs, minWidth: 240 },
  invalidAction: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.xs,
    minHeight: 44,
    paddingHorizontal: spacing.sm,
  },
  invalidActionText: {
    color: central.action.primary,
    fontFamily: central.typography.label,
    fontSize: 14,
  },
  invalidBand: { backgroundColor: central.status.warning.background },
  invalidCopy: {
    alignItems: "center",
    flex: 1,
    flexDirection: "row",
    gap: spacing.sm,
    minWidth: 240,
  },
  invalidDescription: {
    color: central.status.warning.foreground,
    fontFamily: central.typography.body,
    fontSize: 13,
  },
  invalidTitle: {
    color: central.status.warning.foreground,
    fontFamily: central.typography.label,
    fontSize: 14,
  },
  link: {
    alignItems: "center",
    borderRadius: central.radius.control,
    minHeight: 44,
    justifyContent: "center",
    paddingHorizontal: spacing.sm,
  },
  links: { alignItems: "center", gap: spacing.xs },
  linkText: {
    color: central.action.primary,
    fontFamily: central.typography.label,
    fontSize: 13,
  },
  loadingText: {
    color: central.text.secondary,
    fontFamily: central.typography.bodyMedium,
    fontSize: 14,
  },
  lovedOneName: {
    color: central.text.primary,
    fontFamily: central.typography.label,
    fontSize: 16,
    maxWidth: 320,
  },
  metadata: { alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: spacing.md },
  metadataItem: { alignItems: "center", flexDirection: "row", gap: spacing.xs, maxWidth: 260 },
  metadataText: {
    color: central.text.secondary,
    fontFamily: central.typography.body,
    fontSize: 13,
  },
  primaryIdentity: { alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: spacing.xs },
  statusBadge: {
    backgroundColor: central.status.info.background,
    borderRadius: central.radius.badge,
    paddingHorizontal: spacing.xs,
    paddingVertical: 3,
  },
  statusText: {
    color: central.status.info.foreground,
    fontFamily: central.typography.label,
    fontSize: 11,
  },
});
